import { FileUploadInput, POLICY_MAX_FILES_PER_UPLOAD, PolicyDocumentUploadResponse } from '@shared/types/project.type';
import { getClientCredentials } from '../../../../../app/shared/utils/cookie.utils';

/** Allowed extensions for audio deepfake upload (per API doc) */
const AUDIO_DEEPFAKE_EXTENSIONS = ['.wav', '.mp3', '.m4a', '.flac', '.ogg', '.webm'];

/** API requires this value for POST /upload-audio-deepfake */
export const AUDIO_DEEPFAKE_UPLOAD_TYPE = 'AUDIO_DEEPFAKE' as const;

export interface UploadAudioDeepfakeInput {
  file: File;
  projectId: string;
  userId: string;
  uploadType?: typeof AUDIO_DEEPFAKE_UPLOAD_TYPE;
  title?: string;
  domain?: string;
}

const POLICY_EXTENSIONS = ['.pdf', '.docx'] as const;

function applyAuthHeaders(xhr: XMLHttpRequest, userIdHeader?: string | null) {
  const token = localStorage.getItem('accessToken');
  if (token) {
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
  }

  const environment = (localStorage.getItem('SELECTED_ENV') || 'DEV') as 'DEV' | 'PROD';
  const credentials = getClientCredentials(environment);
  if (credentials) {
    xhr.setRequestHeader('X-Client-Id', credentials.clientId);
    if (credentials.clientSecret) {
      xhr.setRequestHeader('X-Client-Secret', credentials.clientSecret);
    }
  }

  const userId = userIdHeader ?? localStorage.getItem('userId');
  if (userId) {
    xhr.setRequestHeader('X-User-Id', userId);
  }
}

function parseXhrJson(xhr: XMLHttpRequest): any {
  if (!xhr.responseText) return xhr.response;
  try {
    return JSON.parse(xhr.responseText);
  } catch {
    return xhr.response;
  }
}

function extractErrorMessage(xhr: XMLHttpRequest, fallback: string): string {
  try {
    const errorResponse = xhr.responseText ? JSON.parse(xhr.responseText) : null;
    if (errorResponse?.message) {
      const errorMessage = errorResponse.message as string;
      if (xhr.status === 500 && errorMessage.includes("name 'client' is not defined")) {
        return 'Server error: Document processing service is not properly configured. Please contact support.';
      }
      if (xhr.status === 500) {
        return `Server error: ${errorMessage}. Please try again or contact support if the issue persists.`;
      }
      return errorMessage;
    }
    if (errorResponse?.error) return errorResponse.error;
  } catch {
    /* ignore parse error */
  }
  return xhr.statusText || fallback;
}

export const uploadAudioDeepfake = (
  input: UploadAudioDeepfakeInput,
  onProgress: (progress: number, status: string) => void
): Promise<any> => {
  return new Promise((resolve, reject) => {
    const ext = input.file.name.toLowerCase().slice(input.file.name.lastIndexOf('.'));
    if (!AUDIO_DEEPFAKE_EXTENSIONS.includes(ext)) {
      reject(new Error(`Invalid file format. Allowed: ${AUDIO_DEEPFAKE_EXTENSIONS.join(', ')}`));
      return;
    }

    const formData = new FormData();
    formData.append('file', input.file);
    formData.append('projectId', input.projectId);
    formData.append('userId', input.userId);
    formData.append('uploadType', input.uploadType ?? AUDIO_DEEPFAKE_UPLOAD_TYPE);
    if (input.title) formData.append('title', input.title);
    if (input.domain) formData.append('domain', input.domain);

    const baseUrl = process.env.REACT_APP_API_BASE_URL?.replace(/\/+$/, '') || '';
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${baseUrl}/upload-audio-deepfake`);
    applyAuthHeaders(xhr, input.userId);

    xhr.upload.onprogress = event => {
      if (event.lengthComputable) {
        const pct = Math.round((event.loaded * 100) / event.total);
        onProgress(pct, 'Uploading');
      }
    };

    xhr.onload = () => {
      if (xhr.status === 200) {
        onProgress(100, 'Completed');
        resolve(parseXhrJson(xhr));
      } else {
        onProgress(100, 'Failed');
        reject(new Error(extractErrorMessage(xhr, 'Audio upload failed')));
      }
    };

    xhr.onerror = () => {
      onProgress(100, 'Failed');
      reject(new Error('Audio upload failed'));
    };

    xhr.send(formData);
  });
};

function resolvePolicyFiles(input: FileUploadInput): File[] {
  if (input.files?.length) return input.files;
  if (input.file) return [input.file];
  return [];
}

function validatePolicyUpload(input: FileUploadInput, files: File[]): string | null {
  if (!input.domain) return 'Domain is required for policy document uploads';
  if (!input.projectId) return 'Project ID is required for policy document uploads';
  if (!input.userId) return 'User ID is required for policy document uploads';
  if (!files.length) return 'At least one file is required for policy document uploads';
  if (files.length > POLICY_MAX_FILES_PER_UPLOAD) {
    return `Maximum ${POLICY_MAX_FILES_PER_UPLOAD} policy documents can be uploaded in one request`;
  }

  for (const file of files) {
    const lower = file.name.toLowerCase();
    const ok = POLICY_EXTENSIONS.some(ext => lower.endsWith(ext));
    if (!ok) {
      return `Only PDF and DOCX files are supported for policy documents (invalid: ${file.name})`;
    }
  }
  return null;
}

function isLegacyFileFieldRequired(status: number, response: any): boolean {
  if (status !== 400) return false;
  const message = String(response?.message ?? response?.error ?? '').toLowerCase();
  // Staging still requires singular `file`; multi-upload API requires `files`.
  return message.includes('file') && message.includes('required') && !message.includes('files');
}

/** Normalize multi-upload and legacy single-upload success payloads. */
export function normalizePolicyUploadResponse(response: any, fallbackFileName?: string): PolicyDocumentUploadResponse {
  if (response && Array.isArray(response.accepted_uploads)) {
    const acceptedUploads = response.accepted_uploads;
    const failedUploads = Array.isArray(response.failed_uploads) ? response.failed_uploads : [];
    return {
      status: Number(response.status) || 200,
      message: response.message || 'Policy documents uploaded successfully',
      projectId: response.projectId,
      organization_id: response.organization_id,
      domain: response.domain,
      accepted_count: Number(response.accepted_count ?? acceptedUploads.length) || acceptedUploads.length,
      failed_count: Number(response.failed_count ?? failedUploads.length) || failedUploads.length,
      accepted_uploads: acceptedUploads,
      failed_uploads: failedUploads
    };
  }

  const legacyMeta = response?.metadata ?? response?.data ?? response;
  const uploadId = legacyMeta?.upload_id ?? response?.upload_id;
  if (uploadId) {
    const fileName = legacyMeta?.file_name || fallbackFileName || 'policy-document';
    return {
      status: 200,
      message: response?.message || 'Policy document uploaded successfully',
      projectId: legacyMeta?.projectId ?? response?.projectId,
      domain: legacyMeta?.domain ?? response?.domain,
      accepted_count: 1,
      failed_count: 0,
      accepted_uploads: [
        {
          upload_id: String(uploadId),
          file_name: fileName,
          chunk_count: Number(legacyMeta?.chunk_count) || 0,
          policy_processing_status: legacyMeta?.policy_processing_status || 'PROCESSING',
          rag_enabled: Boolean(legacyMeta?.rag_enabled)
        }
      ],
      failed_uploads: []
    };
  }

  return {
    status: Number(response?.status) || 400,
    message: response?.message || 'No policy documents were accepted',
    accepted_count: 0,
    failed_count: 1,
    accepted_uploads: [],
    failed_uploads: fallbackFileName
      ? [{ file_name: fallbackFileName, error: response?.message || 'Upload failed' }]
      : []
  };
}

function postPolicyFormData(
  formData: FormData,
  userId: string,
  onProgress: (progress: number, status: string) => void,
  progressScale: { start: number; end: number } = { start: 0, end: 100 }
): Promise<{ status: number; response: any }> {
  return new Promise((resolve, reject) => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL?.replace(/\/+$/, '') || '';
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${baseUrl}/upload-policy-document`);
    applyAuthHeaders(xhr, userId);

    const span = Math.max(1, progressScale.end - progressScale.start);

    xhr.upload.onprogress = event => {
      if (event.lengthComputable) {
        const pct = progressScale.start + Math.round((event.loaded * (span * 0.7)) / event.total);
        onProgress(Math.min(progressScale.end - 1, pct), 'Uploading');
      }
    };

    xhr.onloadstart = () => {
      onProgress(progressScale.start + Math.round(span * 0.7), 'Processing');
    };

    xhr.onload = () => {
      resolve({ status: xhr.status, response: parseXhrJson(xhr) });
    };

    xhr.onerror = () => {
      reject(new Error('Policy upload failed'));
    };

    xhr.send(formData);
  });
}

function buildPolicyFormData(input: FileUploadInput, mode: 'files' | 'file', files: File[]): FormData {
  const formData = new FormData();
  if (mode === 'files') {
    files.forEach(file => formData.append('files', file));
  } else {
    formData.append('file', files[0]);
  }
  formData.append('projectId', input.projectId);
  formData.append('userId', input.userId);
  formData.append('uploadType', 'POLICY_DOCUMENT');
  formData.append('domain', input.domain as string);
  if (input.title) formData.append('title', input.title);
  if (input.organizationId) formData.append('organizationId', input.organizationId);
  return formData;
}

async function uploadPolicyDocumentsLegacySequential(
  input: FileUploadInput,
  files: File[],
  onProgress: (progress: number, status: string) => void
): Promise<PolicyDocumentUploadResponse> {
  const accepted_uploads: PolicyDocumentUploadResponse['accepted_uploads'] = [];
  const failed_uploads: PolicyDocumentUploadResponse['failed_uploads'] = [];

  for (let i = 0; i < files.length; i += 1) {
    const file = files[i];
    const start = Math.round((i / files.length) * 100);
    const end = Math.round(((i + 1) / files.length) * 100);
    onProgress(start, `Uploading ${i + 1}/${files.length}`);

    try {
      const { status, response } = await postPolicyFormData(
        buildPolicyFormData(input, 'file', [file]),
        input.userId,
        onProgress,
        { start, end }
      );

      if (status === 200) {
        const normalized = normalizePolicyUploadResponse(response, file.name);
        if (normalized.accepted_count > 0) {
          accepted_uploads.push(...normalized.accepted_uploads);
        } else {
          failed_uploads.push({
            file_name: file.name,
            error: normalized.message || 'Upload failed'
          });
        }
      } else {
        failed_uploads.push({
          file_name: file.name,
          error: response?.message || response?.error || `Upload failed (${status})`
        });
      }
    } catch (error: any) {
      failed_uploads.push({
        file_name: file.name,
        error: error?.message || 'Upload failed'
      });
    }
  }

  const result: PolicyDocumentUploadResponse = {
    status: accepted_uploads.length > 0 ? 200 : 400,
    message:
      accepted_uploads.length > 0 ? 'Policy documents uploaded successfully' : 'No policy documents were accepted',
    accepted_count: accepted_uploads.length,
    failed_count: failed_uploads.length,
    accepted_uploads,
    failed_uploads
  };

  if (accepted_uploads.length === 0) {
    onProgress(0, 'Failed');
    throw Object.assign(new Error(failed_uploads[0]?.error || result.message), { response: result });
  }

  onProgress(100, 'Completed');
  return result;
}

/**
 * POST /upload-policy-document
 * Supports latest multi-upload (`files`) and falls back to legacy single `file`
 * (used by staging) with sequential requests for multi-select.
 */
export const uploadPolicyDocuments = async (
  input: FileUploadInput,
  onProgress: (progress: number, status: string) => void
): Promise<PolicyDocumentUploadResponse> => {
  const files = resolvePolicyFiles(input);
  const validationError = validatePolicyUpload(input, files);
  if (validationError) {
    onProgress(0, 'Failed');
    throw new Error(validationError);
  }

  try {
    const { status, response } = await postPolicyFormData(
      buildPolicyFormData(input, 'files', files),
      input.userId,
      onProgress,
      { start: 0, end: 100 }
    );

    if (status === 200) {
      const normalized = normalizePolicyUploadResponse(response, files[0]?.name);
      if (normalized.accepted_count > 0) {
        onProgress(100, 'Completed');
        return normalized;
      }
      onProgress(0, 'Failed');
      throw Object.assign(new Error(normalized.message || 'No policy documents were accepted'), {
        response: normalized
      });
    }

    if (isLegacyFileFieldRequired(status, response)) {
      return uploadPolicyDocumentsLegacySequential(input, files, onProgress);
    }

    onProgress(0, 'Failed');
    const message =
      response?.message ||
      (Array.isArray(response?.failed_uploads) && response.failed_uploads[0]?.error) ||
      `Policy upload failed (${status})`;
    throw Object.assign(new Error(message), { response });
  } catch (error: any) {
    if (error?.response || error?.message) {
      throw error;
    }
    onProgress(0, 'Failed');
    throw new Error('Policy upload failed');
  }
};

export const uploadFile = (
  input: FileUploadInput,
  onProgress: (progress: number, status: string) => void
): Promise<any> => {
  if (input.uploadType === 'POLICY_DOCUMENT') {
    return uploadPolicyDocuments(input, onProgress);
  }

  return new Promise((resolve, reject) => {
    if (!input.file) {
      reject(new Error('File is required'));
      return;
    }

    const xhr = new XMLHttpRequest();
    const formData = new FormData();
    let END_POINT = 'upload-dataset';
    switch (input.uploadType) {
      case 'DATA_SET':
        END_POINT = 'upload-dataset';
        break;
      case 'ML_MODEL':
        END_POINT = 'upload-ml-model';
        break;
      case 'LLM_MODEL':
        END_POINT = 'upload-llm-sensitive-output';
        break;
      default:
        END_POINT = 'upload-dataset';
        break;
    }

    formData.append('file', input.file);
    formData.append('projectId', input.projectId);
    formData.append('userId', input.userId);
    formData.append('uploadType', input.uploadType ?? '');

    if (input.domain) {
      formData.append('domain', input.domain);
    }
    if (input.targetColumn) {
      formData.append('targetColumn', input.targetColumn);
    }
    if (input.title) {
      formData.append('title', input.title);
    }

    const baseUrl = process.env.REACT_APP_API_BASE_URL?.replace(/\/+$/, '') || '';
    xhr.open('POST', `${baseUrl}/${END_POINT}`);
    applyAuthHeaders(xhr);

    xhr.upload.onprogress = event => {
      if (event.lengthComputable) {
        const uploadProgress = Math.round((event.loaded * 50) / event.total);
        onProgress(uploadProgress, 'Uploading');
      }
    };

    xhr.onloadstart = () => {
      onProgress(50, 'Processing');
    };

    xhr.onload = () => {
      if (xhr.status === 200) {
        onProgress(100, 'Completed');
        resolve(parseXhrJson(xhr));
      } else {
        onProgress(0, 'Failed');
        const errorResponse = parseXhrJson(xhr);
        console.error('Upload error:', {
          status: xhr.status,
          statusText: xhr.statusText,
          response: errorResponse,
          uploadType: input.uploadType
        });
        reject(new Error(extractErrorMessage(xhr, 'Upload failed')));
      }
    };

    xhr.onerror = () => {
      onProgress(0, 'Failed');
      reject(new Error('Upload failed'));
    };

    xhr.send(formData);
  });
};
